import Countdown, {
  type CountdownProps,
  getCountdownNodeStylesObject,
} from '@builder4/shared/components/Countdown';
import { ROOT_NODE, useNode, type Node, type NodeHelpersType } from '@craftjs/core';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { COUNTDOWN_NODE_NAME } from '@/utils/constants';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { InspectorCountdown } from '@/components/inspector/InspectorCountdown/InspectorCountdown';
import { Resizer } from '../Resizer';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { getVisibilityClasses, DEFAULT_VISIBILITY } from '@builder4/shared/utils/styleHelpers';
import clsx from 'clsx';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';

// Wrapper component that provides the Resizer and Craft.js integration to the shared Countdown
const EditorCountdown = (props: Partial<CountdownProps>) => {
  const { getBPProp } = useBreakpointAwareProp(COUNTDOWN_NODE_NAME);

  const visibility = getBPProp(props, 'visibility') ?? DEFAULT_VISIBILITY;
  const fillSpace = getBPProp(props, 'fillSpace');

  const {
    connectors: { connect, drag },
  } = useNode();

  const breakpoint = useAppSelector(selectBreakpoint);

  const visibilityClasses = getVisibilityClasses(visibility);

  // Get inline styles for Resizer (same approach as Container)
  const { mobile, tablet, ...computedStyles } = getCountdownNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = mobile;
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = tablet;
  }

  return (
    <Resizer
      className={clsx('countdown-node pointer-events-none', visibilityClasses, {
        'flex-shrink-1-imp': fillSpace === 'yes',
      })}
      propKey={{ width: 'width', height: 'height' }}
      onRef={(ref) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      maxWidth={'100%'}
      style={currentBpFlexStyle}
    >
      <Countdown {...props} />
    </Resizer>
  );
};

EditorCountdown.craft = {
  ...Countdown.craft,
  related: {
    toolbar: InspectorCountdown,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
  },
};

export default EditorCountdown;
